'use client'

import Image from 'next/image'
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Button } from '@acko/button'
import { OtpInput } from '@acko/otp-input'
import {
  ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA,
  ABHA_HERO_MODAL_RESEND_OTP,
  ABHA_HERO_MODAL_RESEND_OTP_COUNTDOWN_LABEL,
  ABHA_HERO_SUCCESS_TITLE_MOBILE,
} from '@components/NewProduct/constants'
import { ABHA_RESOLVE_NHA_LOGO } from '@components/AbhaResolve/constants'
import { AbhaHeroModalCloseButton } from '@components/NewProduct/AbhaHeroWidget/AbhaHeroModalCloseButton'
import { AbhaHeroCard } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroCard'
import { AbhaHeroSuccessContentSection } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessContentSection'
import { AbhaHeroSuccessActionButton } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessDownloadButton'
import { AbhaHeroSuccessRightPanel } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessRightPanel'
import type { AbhaHeroSuccessStepData } from '@components/NewProduct/AbhaHeroWidget/steps/AbhaHeroSuccessStep'
import { formatOtpResendCountdown } from '@components/NewProduct/AbhaHeroWidget/steps/formatOtpResendCountdown'
import { normalizeSuccessPanelData } from '@components/NewProduct/AbhaHeroWidget/steps/normalizeSuccessPanelData'
import { buildCardPreviewFromMember, type AbhaMemberListDetail } from '@helpers/abhaJourneyHelper'
import { ABHA_OTP_LENGTH, getAbhaProfileInitials } from '@helpers/abhaWidgetHelper'
import { formatAbhaHeroStepTemplate } from '@components/NewProduct/AbhaHeroWidget/steps/abhaHeroStepData'
import type { AbhaSuccessAnalyticsVariant } from '@components/NewProduct/AbhaHeroWidget/abhaHeroAnalytics'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { ABHA_EXISTING_ABHA_SUCCESS_COPY_CONFIG } from '@configs/abhaHeroWidget.config'
import { PLATFORM } from '@configs/platform.config'

export type ExistingAbhaSuccessStepData = AbhaHeroSuccessStepData & {
  pageType?: string
  variantName?: AbhaSuccessAnalyticsVariant
  title?: string
  mobileTitle?: string
  description?: string
  viewCtaText?: string
  downloadCtaText?: string
  mobileViewCtaText?: string
  mobileDownloadCtaText?: string
}

export type AbhaHeroExistingAbhaSuccessStepProps = {
  data?: ExistingAbhaSuccessStepData
  phone: string
  members: AbhaMemberListDetail[]
  isMobile: boolean
  errorMessage?: string
  appDownloadLink: string
  onSelect: (member: AbhaMemberListDetail) => void
  onDownload: (member: AbhaMemberListDetail) => void
  onDownloadApp: () => void
  onOpenDownloadModal?: () => void
  onInactiveCardClick?: () => void
  onViewAbha?: () => void
  ctaAnalyticsProps?: Record<string, unknown>
  isOtpOpen?: boolean
  selectedAccountIndex?: number | null
  otp?: string
  otpError?: string
  otpSubmitError?: string
  resendCountdown?: number
  isVerifyOtpEnabled?: boolean
  isSubmitting?: boolean
  canResendOtp?: boolean
  onOtpChange?: (value: string) => void
  onResendOtp?: () => void
  onVerifyOtp?: (event: FormEvent<HTMLFormElement>) => void
}

const getMemberKey = (member: AbhaMemberListDetail, index: number) =>
  `${String(member?.account_index ?? member?.abha_id ?? member?.id ?? 'member')}-${index}`

const formatMaskedOtpPhone = (maskedPhone: string | undefined, phone: string) => {
  const resolvedMaskedPhone = maskedPhone?.replace(/[()\s-]/g, '')

  if (resolvedMaskedPhone) {
    if (resolvedMaskedPhone.startsWith('+')) return resolvedMaskedPhone
    if (resolvedMaskedPhone.startsWith('91')) return `+${resolvedMaskedPhone}`

    return `+91${resolvedMaskedPhone}`
  }

  const sanitizedPhone = phone.replace(/\D/g, '').slice(-10)

  if (!sanitizedPhone) return ''

  return `+91${'*'.repeat(Math.max(sanitizedPhone.length - 2, 0))}${sanitizedPhone.slice(-2)}`
}

export const AbhaHeroExistingAbhaSuccessStep = ({
  data,
  phone,
  members,
  isMobile,
  errorMessage,
  appDownloadLink,
  onSelect,
  onDownload,
  onDownloadApp,
  onOpenDownloadModal,
  onViewAbha,
  ctaAnalyticsProps,
  isOtpOpen = false,
  selectedAccountIndex,
  otp = '',
  otpError = '',
  otpSubmitError = '',
  resendCountdown = 0,
  isVerifyOtpEnabled = false,
  isSubmitting = false,
  canResendOtp = false,
  onOtpChange,
  onResendOtp,
  onVerifyOtp,
}: AbhaHeroExistingAbhaSuccessStepProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)
  const [downloadedMemberName, setDownloadedMemberName] = useState('')
  const otpInputRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setActiveIndex(currentIndex => Math.min(currentIndex, Math.max(members?.length - 1, 0)))
  }, [members?.length])

  useEffect(() => {
    if (!isMobile || !isOtpOpen) return undefined

    const focusTimerId = window.setTimeout(() => {
      otpInputRef.current?.querySelector<HTMLInputElement>('.acko-otp-cell')?.focus()
    }, 0)

    return () => window.clearTimeout(focusTimerId)
  }, [isMobile, isOtpOpen])

  const cardData = useMemo(() => members.map(buildCardPreviewFromMember), [members])
  const activeMember = members?.[activeIndex]
  const maskedOtpPhone = formatMaskedOtpPhone(
    activeMember?.masked_phone_number ?? members.find(member => member?.masked_phone_number)?.masked_phone_number,
    phone
  )
  const platformCopy = ABHA_EXISTING_ABHA_SUCCESS_COPY_CONFIG[isMobile ? PLATFORM.MOBILE : PLATFORM.DESKTOP]
  const copyInput = { data, fallbackData: ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA }
  const title = platformCopy.title(copyInput)
  const viewCtaText = platformCopy.viewCtaText(copyInput)
  const downloadCtaText = platformCopy.downloadCtaText(copyInput)
  const description = formatAbhaHeroStepTemplate(
    data?.description ?? ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.DESCRIPTION,
    { phone_number: phone }
  )
  const panelData = normalizeSuccessPanelData(data?.mobileData, {
    title: ABHA_HERO_SUCCESS_TITLE_MOBILE,
    appLink: appDownloadLink,
  })
  const hasDownloadModalContent =
    Boolean(panelData.header.title || panelData.header.description) || panelData.content.length > 0

  const handleMobileDownload = (member: AbhaMemberListDetail, index: number) => {
    onDownload(member)
    setDownloadedMemberName(cardData[index]?.name ?? '')
    setIsDownloadModalOpen(true)
    onOpenDownloadModal?.()
  }

  const renderProfileAvatar = (memberCard: (typeof cardData)[number]) => (
    <PropertyControlledComponent
      controllerProperty={!!memberCard?.profileImageUrl}
      fallback={
        <span className="abha-hero-existing-success__profile-avatar-fallback" aria-hidden>
          {getAbhaProfileInitials(memberCard?.name)}
        </span>
      }>
      <Image
        src={memberCard?.profileImageUrl || ''}
        alt=""
        width={40}
        height={40}
        unoptimized={memberCard?.profileImageUrl?.startsWith('data:')}
        className="abha-hero-existing-success__profile-avatar"
        aria-hidden
      />
    </PropertyControlledComponent>
  )

  const renderDesktopCompactCard = (member: AbhaMemberListDetail, index: number) => {
    const memberCard = cardData[index]

    return (
      <button
        key={getMemberKey(member, index)}
        type="button"
        className="abha-hero-existing-success__list-card"
        onClick={() => {
          setActiveIndex(index)
          if (!member?.token) onSelect(member)
        }}>
        <PropertyControlledComponent
          controllerProperty={!!memberCard?.profileImageUrl}
          fallback={
            <span className="abha-hero-existing-success__list-avatar-fallback" aria-hidden>
              {getAbhaProfileInitials(memberCard?.name)}
            </span>
          }>
          <Image
            src={memberCard?.profileImageUrl || ''}
            alt=""
            width={52}
            height={52}
            unoptimized={memberCard?.profileImageUrl?.startsWith('data:')}
            className="abha-hero-existing-success__list-avatar"
            aria-hidden
          />
        </PropertyControlledComponent>
        <span className="abha-hero-existing-success__list-identity">
          <strong>{memberCard?.name}</strong>
          {memberCard?.dateOfBirth && <span>{memberCard.dateOfBirth}</span>}
        </span>
        <span className="abha-hero-existing-success__list-arrow" aria-hidden>
          ›
        </span>
      </button>
    )
  }

  const renderMobileCompactCard = (member: AbhaMemberListDetail, index: number) => {
    const memberCard = cardData[index]

    return (
      <article key={getMemberKey(member, index)} className="abha-hero-existing-success__profile-card">
        <div className="abha-hero-existing-success__profile-header">
          <div className="abha-hero-existing-success__profile-identity">
            {renderProfileAvatar(memberCard)}
            <strong>{memberCard?.name}</strong>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="abha-hero-existing-success__profile-view"
            disabled={isSubmitting}
            onClick={() => {
              setActiveIndex(index)

              if (member?.token) {
                handleMobileDownload(member, index)
                return
              }

              onViewAbha?.()
              onSelect(member)
            }}>
            {member?.token ? downloadCtaText : viewCtaText}
          </Button>
        </div>
      </article>
    )
  }

  const renderInlineOtpCard = (member: AbhaMemberListDetail, index: number) => {
    const memberCard = cardData[index]

    return (
      <article key={getMemberKey(member, index)} className="abha-hero-existing-success__profile-card is-expanded">
        <div className="abha-hero-existing-success__profile-header">
          <div className="abha-hero-existing-success__profile-identity">
            {renderProfileAvatar(memberCard)}
            <strong>{memberCard?.name}</strong>
          </div>
        </div>

        <div className="abha-hero-existing-success__profile-separator" />

        <form
          className="abha-hero-existing-success__profile-otp-form"
          onSubmit={event => {
            event.preventDefault()
            onVerifyOtp?.(event)
          }}>
          <div ref={otpInputRef}>
            <OtpInput
              className="abha-hero-existing-success__profile-otp"
              label={`Enter OTP sent to ${phone}`}
              value={otp}
              length={ABHA_OTP_LENGTH}
              size="sm"
              error={Boolean(otpError)}
              errorText={otpError || undefined}
              onChange={value => onOtpChange?.(value)}
            />
          </div>

          <div className="abha-hero-existing-success__profile-otp-actions">
            <PropertyControlledComponent
              controllerProperty={canResendOtp}
              fallback={
                <span className="abha-hero-existing-success__profile-resend is-disabled">
                  {`${ABHA_HERO_MODAL_RESEND_OTP_COUNTDOWN_LABEL} ${formatOtpResendCountdown(resendCountdown)}`}
                </span>
              }>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="abha-hero-existing-success__profile-resend"
                onClick={() => onResendOtp?.()}>
                {ABHA_HERO_MODAL_RESEND_OTP}
              </Button>
            </PropertyControlledComponent>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              className="abha-hero-existing-success__profile-verify"
              disabled={!isVerifyOtpEnabled || isSubmitting}>
              {isSubmitting ? 'Verifying…' : 'Verify'}
            </Button>
          </div>

          {otpSubmitError && <p className="abha-hero-existing-success__profile-otp-error">{otpSubmitError}</p>}
        </form>
      </article>
    )
  }

  const renderVerifiedCard = (member: AbhaMemberListDetail, index: number) => {
    const memberCard = cardData[index]

    return (
      <article
        key={getMemberKey(member, index)}
        className="abha-hero-existing-success__profile-card is-expanded is-verified">
        <div className="abha-hero-existing-success__profile-header">
          <div className="abha-hero-existing-success__profile-identity">
            {renderProfileAvatar(memberCard)}
            <span className="abha-hero-existing-success__profile-verified-identity">
              <strong>{memberCard?.name}</strong>
              {memberCard?.dateOfBirth && <span>{memberCard.dateOfBirth}</span>}
            </span>
          </div>
        </div>

        <div className="abha-hero-existing-success__profile-separator" />

        <div className="abha-hero-existing-success__profile-verified-details">
          <div className="abha-hero-existing-success__profile-abha-details">
            <p>{memberCard?.abhaNumber ?? memberCard?.maskedAbhaNumber}</p>
            <span>{memberCard?.abhaAddress}</span>
            <Image
              src={ABHA_RESOLVE_NHA_LOGO}
              alt="National Health Authority"
              width={56}
              height={28}
              className="abha-hero-existing-success__profile-nha-logo"
            />
          </div>
          <AbhaHeroSuccessActionButton
            label={downloadCtaText}
            onClick={() => handleMobileDownload(member, index)}
            fullWidth
            className="abha-hero-existing-success__profile-download"
          />
        </div>
      </article>
    )
  }

  if (!isMobile) {
    return (
      <div className="abha-hero-success abha-hero-success--desktop abha-hero-existing-success">
        <section className="abha-hero-existing-success__left">
          <div className="abha-hero-existing-success__left-content">
            <div className="abha-hero-existing-success__heading">
              <h2>{title}</h2>
              {description && members?.length > 1 && <p>{description}</p>}
              {errorMessage && <p className="abha-hero-existing-success__error">{errorMessage}</p>}
            </div>

            <div className="abha-hero-existing-success__desktop-list">
              <div className="abha-hero-existing-success__compact-list">
                {members.map((member, index) => {
                  const memberCard = cardData[index]

                  if (!member?.token || !memberCard) return renderDesktopCompactCard(member, index)

                  return (
                    <div key={getMemberKey(member, index)} className="abha-hero-existing-success__verified-panel">
                      <AbhaHeroCard
                        data={memberCard}
                        layout="resolve"
                        variant="showAbhaNumber"
                        animateReveal={false}
                        className="abha-hero-existing-success__verified-card"
                      />
                      <AbhaHeroSuccessActionButton
                        label={data?.downloadCtaText ?? ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.DOWNLOAD_CTA_TEXT}
                        onClick={() => onDownload(member)}
                        fullWidth
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        <AbhaHeroSuccessRightPanel
          data={data}
          appDownloadLink={appDownloadLink}
          ctaAnalyticsProps={ctaAnalyticsProps}
        />
      </div>
    )
  }

  return (
    <div className="abha-hero-existing-success abha-hero-existing-success--mobile">
      <section className="abha-hero-existing-success__mobile-top">
        <div className="abha-hero-existing-success__mobile-heading">
          <h2>{title}</h2>
          {maskedOtpPhone && (
            <p className="abha-hero-existing-success__otp-helper">
              To access the cards, enter the OTP sent to ({maskedOtpPhone})
            </p>
          )}
        </div>
        {errorMessage && <p className="abha-hero-existing-success__error">{errorMessage}</p>}

        <div className="abha-hero-existing-success__profile-list">
          {members.map((member, index) => {
            const isSelectedForOtp =
              isOtpOpen && selectedAccountIndex !== null && Number(member?.account_index) === selectedAccountIndex
            const isVerified = Boolean(member?.token)

            if (isSelectedForOtp) return renderInlineOtpCard(member, index)
            if (isVerified) return renderVerifiedCard(member, index)

            return renderMobileCompactCard(member, index)
          })}
        </div>
      </section>

      {isDownloadModalOpen && (
        <div className="abha-hero-existing-download-overlay">
          <section
            className="abha-hero-existing-download-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="abha-download-initiated-title">
            <div className="abha-hero-existing-download-modal__header">
              <AbhaHeroModalCloseButton
                onClick={() => setIsDownloadModalOpen(false)}
                size="md"
                className="abha-hero-existing-download-modal__close"
              />
            </div>

            <div className="abha-hero-existing-download-modal__alert" role="status">
              <Image
                src={ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.DOWNLOAD_MODAL_STATUS_ICON}
                alt=""
                width={16}
                height={16}
                className="abha-hero-existing-download-modal__status-icon"
                aria-hidden
              />
              <div>
                <p id="abha-download-initiated-title">
                  {ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.DOWNLOAD_MODAL_TITLE}
                </p>
                <span>
                  {downloadedMemberName
                    ? ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.DOWNLOAD_MODAL_DESCRIPTION.replace(
                        '{{member_name}}',
                        downloadedMemberName
                      )
                    : ABHA_HERO_EXISTING_ABHA_SUCCESS_STEP_DATA.DOWNLOAD_MODAL_DESCRIPTION_FALLBACK}
                </span>
              </div>
            </div>

            {hasDownloadModalContent && (
              <section
                className={`abha-hero-existing-download-modal__content abha-hero-success-panel--${panelData.panelVariant}`}>
                {(panelData.header.title || panelData.header.description) && (
                  <div className="abha-hero-success__mobile-show-bottom-header">
                    {panelData.header.title && (
                      <h2 className="abha-hero-success__mobile-show-bottom-title">{panelData.header.title}</h2>
                    )}
                    {panelData.header.description && (
                      <p className="abha-hero-success__mobile-show-bottom-description">
                        {panelData.header.description}
                      </p>
                    )}
                  </div>
                )}
                <AbhaHeroSuccessContentSection
                  panelData={panelData}
                  appDownloadLink={appDownloadLink}
                  isMobile
                  onDownloadApp={onDownloadApp}
                  ctaAnalyticsProps={ctaAnalyticsProps}
                />
              </section>
            )}
          </section>
        </div>
      )}
    </div>
  )
}
